import { action } from 'mobx';

import Guid from 'bz-utilities/modules/variables/guid';

import moment from 'moment';
import Socket from '../../devices';
import type IPayDevice from '../../interfaces/pay-device';
import type IRequest from './dtos/request';
import type IResponse from './dtos/response';
import CardIssuerCode from './enums/card-issuer-code';
import CardVerificationMethod from './enums/card-verification-method';
import EntryMethod from './enums/entry-method';
import InputAttribute from './enums/input-attributes';
import OutputAttributes from './enums/output-attributes';
import TransactionPurpose from './enums/transaction-purpose';
import TransactionResult from './enums/transaction-result';
import TransactionType from './enums/transaction-type';

class iPP350 implements IPayDevice {
  private socket: Socket;
  private promises = new PromiseStore();

  public constructor(port: number) {
    this.socket = new Socket(port, 'iPP350');
    this.socket.onError = this._onError;
    this.socket.onMessage = this._onMessage;
    this.socket.onConnected = this._onConnect;
    this.socket.onDisconnected = this._onDisconnect;
  }

  public connect(): Promise<unknown> {
    let promise = this.promises.create('connect');
    this.socket.connect();
    return promise;
  }

  public disconnect(): Promise<unknown> {
    let promise = this.promises.create('disconnect');
    this.socket.disconnect();
    return promise;
  }

  private send(req: IRequest) {
    if (this.socket.readyState !== 1) {
      return Promise.reject();
    }

    let msg = this.toRequest(req);
    let promise = this.promises.create(req.TransactionRef);

    this.socket.send(msg + '\n99=0\n');

    return promise;
  }

  @action.bound
  private _onConnect() {
    let req: IRequest = {
      TransactionRef: 'connect',
      TransactionType: TransactionType.CheckPinpadConnection
    };

    let msg = this.toRequest(req);

    this.socket.send(msg + '\n99=0\n');
  }

  @action.bound
  private _onMessage(msg: string) {
    let req = JSON.parse(msg);

    switch (req.mode) {
      case 'error':
      case 'disconnect':
        // currently stubs for future
        break;
      case 'message':
        let res = this.toResponse(req.data);
        let tRes = parseInt(TransactionResult[res.TransactionResult]);

        switch (tRes) {
          case TransactionResult.ApprovedManual:
          case TransactionResult.ApprovedOnline:
          case TransactionResult.ApprovedOffline:
          case TransactionResult.PinpadConnected:
          case TransactionResult.PreSalesCompleted:
          case TransactionResult.TransactionPerformed:
            this.promises.resolve(res.TransactionReference!, res);
            break;
          case TransactionResult.StatusBusy:
            this.promises.reject('check-status', res);
            break;
          case TransactionResult.StatusNotBusy:
            this.promises.resolve('check-status', res);
            break;
          default:
            this.promises.reject(res.TransactionReference!, res);
        }
        break;
      default:
        throw 'Unknown message type';
    }
  }

  @action.bound
  private _onError(evt: any) {
    this.promises.rejectAll(evt);
  }

  @action.bound
  private _onDisconnect(evt: any) {
    if (evt.code !== 1000) {
      return;
    }

    this.promises.resolve('disconnect', evt);
  }

  private toRequest(req: IRequest) {
    return Object.entries(req)
      .map((x) => {
        let [key, value] = x;
        let num = InputAttribute[key];
        return `${num}=${value}`;
      })
      .join('\n');
  }

  private toResponse(req: string): IResponse {
    return req
      .replace(/99=0.*$/gm, '')
      .split('\n')
      .filter((x: string) => !!x)
      .reduce((acc: any, curr: any) => {
        let [key, val] = curr.split('=');
        acc[OutputAttributes[key]] = this.parseVal(key, val);
        return acc;
      }, {});
  }

  private parseVal(key: any, val: any) {
    let _key = parseInt(key);
    let _val = parseFloat(val) || val;

    switch (_key) {
      case 1:
        return EntryMethod[_val];
      case 2:
        return TransactionType[_val];
      case 3:
        return TransactionResult[_val];
      case 14:
        return CardVerificationMethod[_val];
      case 60:
        return CardIssuerCode[_val];
      default:
        return val;
    }
  }

  public isBusy(): Promise<unknown> {
    let req: IRequest = {
      TransactionRef: 'check-status',
      TransactionType: TransactionType.CheckStatus
    };

    return this.send(req);
  }

  private getExpiryDateFromResponse(expiryDate: string) {
    const month = expiryDate.substring(0, 2);
    const year = expiryDate.substring(2, 4);
    const days = moment(`${month}-20${year}`, 'MM-YYYY').daysInMonth();

    const date = moment(`${days}/${month}/20${year}`, 'DD/MM/YYYY');

    return date;
  }

  public handset(): Promise<unknown> {
    let req: IRequest = {
      TransactionRef: Guid.create(),
      TransactionType: TransactionType.CheckPinpadConnection
    };

    return this.send(req);
  }

  public verify(): Promise<unknown> {
    let req: IRequest = {
      TransactionRef: Guid.create(),
      TransactionType: TransactionType.AccountVerificationRequest,
      TransactionPurpose: TransactionPurpose.AdHoc
    };

    return new Promise((resolve, reject) => {
      this.send(req)
        .then((res: IResponse) => {
          const last4 = res.EMVPAN.substring(res.EMVPAN.length - 4, res.EMVPAN.length);
          let obj = {
            PAN: last4,
            token: res.TokenReference,
            merchant: 'worldpay',
            expiryDate: this.getExpiryDateFromResponse(res.CardExpiryDate)
          };
          resolve(obj);
        })
        .catch(reject);
    });
  }

  public chargeToken(token: string, amount: number): Promise<unknown> {
    let req: IRequest = {
      TransactionRef: Guid.create(),
      TransactionType: TransactionType.SaleWithToken,
      TransactionPurpose: TransactionPurpose.AdHoc,
      TokenReference: token,
      TransactionAmount: amount
    };

    return this.send(req);
  }

  public payment(amount: number): Promise<unknown> {
    let req: IRequest = {
      TransactionRef: Guid.create(),
      TransactionType: TransactionType.Sale,
      TransactionAmount: amount
    };

    return this.send(req);
  }

  public refund(amount: number): Promise<unknown> {
    let req: IRequest = {
      TransactionRef: Guid.create(),
      TransactionType: TransactionType.RefundWithToken,
      TransactionAmount: amount
    };

    return this.send(req);
  }

  public cancellation(res: IResponse): Promise<unknown> {
    let req: IRequest = {
      TransactionRef: Guid.create(),
      TransactionType: TransactionType.CancelTransaction,
      MaskedPAN: res.EMVPAN,
      CardExpiryDate: parseInt(res.CardExpiryDate || ''),
      PGTR: res.PGTR
    };

    return this.send(req);
  }

  public keyedTransaction(amount: number) {
    let req: IRequest = {
      TransactionRef: Guid.create(),
      TransactionType: TransactionType.Sale,
      TransactionAmount: amount,
      CNP: 1
    };

    return this.send(req);
  }
}

class PromiseStore {
  private promises: { [s: string]: IPromiseResult } = {};

  public create(key: string) {
    return new Promise((resolve: any, reject: any) => {
      this.promises[key] = { resolve, reject };
    });
  }

  public resolve(key: string, args?: object) {
    let promise = this.promises[key];
    delete this.promises[key];
    if (!promise) {
      return;
    }
    promise.resolve(args);
  }

  public reject(key: string, args?: object) {
    let promise = this.promises[key];
    delete this.promises[key];
    if (!promise) {
      return;
    }
    promise.reject(args);
  }

  public rejectAll(args?: object) {
    Object.values(this.promises).forEach((x: IPromiseResult) => {
      x.reject(args);
    });

    this.promises = {};
  }
}

interface IPromiseResult {
  resolve: any;
  reject: any;
}

export default iPP350;
